import React from 'react';
import PlanCard from './PlanCard';
import PlanCardSkeleton from './PlanCardSkeleton';
import { ViewEPPIcon } from '../assets/svg/ViewEPPIcon';
import { installMentPlans } from '@rb-serve-app/core';

interface PlanSectionProps {
  title: string;
  plans: installMentPlans[] | undefined;
  loading: boolean;
  borderColor: string;
  planType: 'active' | 'pending' | 'cancelled' | 'completed' | 'closes';
  noPlansMessage: string;
  className?: string;
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  assets?: any;
  handleViewEPP: (plan: installMentPlans) => void;
}

const PlanSection: React.FC<PlanSectionProps> = ({
  title,
  plans,
  loading,
  borderColor,
  planType,
  noPlansMessage,
  className = '',
  isCollapsible = false,
  isExpanded = true,
  onToggle,
  assets,
  handleViewEPP,
}) => {
  const shouldShowContent = !isCollapsible || isExpanded;
  const skeletonCount = planType === 'active' ? 3 : 2;
  const skeletonType = planType === 'closes' ? 'completed' : planType;

  return (
    <>
      <h2
        aria-label={title}
        id={`${planType}-heading`}
        className={`text-sm font-semibold mt-0 md:mt-5 md:bg-slate-50 md:p-0 w-full px-4 py-[10px] ${className} ${
          isCollapsible
            ? 'cursor-pointer flex justify-between items-center'
            : ''
        }`}
        onClick={isCollapsible ? onToggle : undefined}
      >
        <span>{title}</span>
        {isCollapsible && (
          <span
            className={`transform transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          >
            <img
              src={assets?.down_arrow}
              alt="down-arrow"
              className="w-4 h-4"
            />
          </span>
        )}
      </h2>

      {shouldShowContent && (
        <>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 lg:grid-cols-2 gap-4 mt-4 mb-4 md:mb-0">
              <PlanCardSkeleton
                count={skeletonCount}
                type={
                  skeletonType as
                    | 'active'
                    | 'pending'
                    | 'completed'
                    | 'cancelled'
                }
              />
            </div>
          ) : plans && plans.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 lg:grid-cols-2 gap-4 mt-4 mb-4 md:mb-0">
              {plans.map((plan: any, index: number) => (
                <div
                  key={plan?.planId}
                  aria-label={`${plan?.planType}-heading`}
                >
                  <PlanCard
                    key={index}
                    plan={plan}
                    borderColor={borderColor}
                    plantype={planType}
                    handleViewEPP={handleViewEPP}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col pt-10 rounded-lg border w-full h-44 items-center gap-4 flex-shrink-0 mt-4 mb-4 md:mb-0">
              <ViewEPPIcon fill="#ccc" hoverFill="#fff" />
              <span aria-label={noPlansMessage}>{noPlansMessage}</span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PlanSection;
